<script>
import countlyVue from '../../compat/vue-core';
import i18n from '../../../../i18n';

export default countlyVue.components.BaseComponent.extend({
  props: {
    cancelLabel: { type: String, default: i18n.tc('events.general.cancel') },
    confirmLabel: { type: String, default: i18n.tc('events.general.confirm') },
    resetLabel: { type: String, default: 'Reset Filters' },
    adaptiveLength: { type: Boolean, default: true },
    selectXWidth: { type: Number, default: 320 },
    emptyValue: {
      type: String,
      default() {
        return 'all';
      },
    },
    dependantFields: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    arrow: { type: Boolean, default: false },
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    fields: {
      type: Array,
      default() {
        return [];
      },
    },
    title: { type: String, default: 'Filter Parameters' },
  },
  computed: {
    optionLabel() {
      return function (field, option) {
        const opt = (field.items || field.options || []).find((item) => item.value === option);
        if (opt) {
          return opt.label;
        }

        const opts = field.items || field.options || [];
        if (opts.length > 0) {
          this.unsavedValue[field.key] = opts[0].key;
          return opts[0].label;
        }

        return '';
      };
    },
    dropdownLabel() {
      const forLabel = [];
      if (this.dependantFields) { // do not add values from next one if previous is not chosen.
        const keys = Object.keys(this.fields);
        for (let i = 0; i < keys.length; i += 1) {
          const k = keys[i];
          forLabel.push(this.optionLabel(this.fields[k], this.value[this.fields[k].key]));
          if (this.value[this.fields[k].key] === this.emptyValue) {
            break;
          }
        }
        return forLabel.join(', ');
      }

      return this.fields.map((field) => this.optionLabel(field, this.value[field.key])).join(', ');
    },
  },
  data() {
    return {
      unsavedValue: { ...this.value },
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.unsavedValue = { ...newValue };
      },
    },
  },
  methods: {
    close(dontSync) {
      if (!dontSync) {
        this.unsavedValue = { ...this.value };
      }
      if (this.$refs.selectX && this.$refs.selectX.length) {
        this.$refs.selectX.forEach((component) => {
          component.doClose();
        });
      }
      this.$refs.dropdown.handleClose();
    },
    save() {
      this.$emit('input', this.unsavedValue);
      this.$emit('change', this.unsavedValue);
      this.close();
    },
    showThis(key) {
      if (this.dependantFields) {
        const keys = Object.keys(this.fields);
        for (let i = 0; i < keys.length; i += 1) {
          const z = keys[i];
          if (this.fields[z].key === key) {
            return true;
          }
          if (this.unsavedValue[this.fields[z].key] === this.emptyValue) { // we have unfilled one before
            return false;
          }
        }
      } else {
        return true;
      }
      return false;
    },
    reset() {
      this.fields.forEach((field) => {
        if ('default' in field) {
          this.$set(this.unsavedValue, field.key, field.default);
        }
      });
      this.save();
    },
  },
});
</script>
<template>
  <cly-dropdown
    ref="dropdown"
    class="cly-multi-select__dropdown"
    v-on="$listeners"
  >
    <template #trigger="dropdown">
      <slot name="trigger">
        <cly-input-dropdown-trigger
          ref="trigger"
          :disabled="false"
          :adaptive-length="adaptiveLength"
          :focused="dropdown.focused"
          :opened="dropdown.visible"
          :arrow="arrow"
          :placeholder="dropdownLabel"
        />
      </slot>
    </template>
    <div class="cly-multi-select default-skin">
      <div class="cly-multi-select__body">
        <div>
          <div class="cly-multi-select__title-wrapper">
            <span class="cly-multi-select__title">{{ title }}</span>
            <el-button
              class="cly-multi-select__reset"
              type="text"
              @click="reset"
            >
              {{ resetLabel }}
            </el-button>
          </div>
          <table
            v-for="field in fields"
            :key="field.key"
          >
            <tr
              v-if="showThis(field.key)"
              class="cly-multi-select__field"
            >
              {{ field.label }}
            </tr>
            <tr v-if="'items' in field && showThis(field.key)">
              <el-select
                v-model="unsavedValue[field.key]"
                class="cly-multi-select__field-dropdown"
                :placeholder="optionLabel(field, unsavedValue[field.key])"
                style="margin-top:2px"
              >
                <el-option
                  v-for="item in field.items"
                  :key="item.value"
                  :value="item.value"
                  :label="item.label"
                />
              </el-select>
            </tr>
            <tr v-else-if="'options' in field">
              <cly-select-x
                ref="selectX"
                v-bind="field"
                v-model="unsavedValue[field.key]"
                class="cly-multi-select__field-dropdown"
                :width="selectXWidth"
                :placeholder="optionLabel(field, unsavedValue[field.key])"
              >
                <template
                  v-if="field.header"
                  #header="headerScope"
                >
                  <slot
                    name="header"
                    v-bind="headerScope"
                  />
                </template>
                <template
                  v-if="field.trigger"
                  #trigger="triggerScope"
                >
                  <slot
                    name="trigger"
                    v-bind="triggerScope"
                  />
                </template>
                <template
                  v-if="field.action"
                  #action
                >
                  <slot name="action" />
                </template>
              </cly-select-x>
            </tr>
          </table>
        </div>
        <div class="cly-multi-select__controls">
          <el-button
            v-bind="$attrs"
            class="el-button el-button--secondary el-button--small"
            @click="close"
          >
            {{ cancelLabel }}
          </el-button>
          <el-button
            v-bind="$attrs"
            class="el-button el-button--success el-button--small"
            @click="save"
          >
            {{ confirmLabel }}
          </el-button>
        </div>
      </div>
    </div>
  </cly-dropdown>
</template>
